import { SlashMenu as AssemblySlashMenu } from '@assembly-js/design-system';
import { Editor } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import ListKeymap from '@tiptap/extension-list-keymap';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import React, { useEffect } from 'react';

import { ClearMarksOnEnter } from 'src/components/tiptap/custom-extensions/ClearMarksOnEnter';
import { SlashCommand } from 'src/components/tiptap/custom-extensions/SlashCommand';
import { SlashMenuRenderer } from 'src/components/tiptap/custom-extensions/SlashMenuRenderer';
import { RichTextInput } from 'src/components/tiptap/RichTextInput';
import { getSlashMenuSuggestionItems } from 'src/features/notes/NoteModal/utils';

// Supported toolbar actions
const PAYMENT_EDITOR_TOOLBAR_ACTIONS = [
  'heading2',
  'heading3',
  'bold',
  'italic',
  'underline',
  'strike',
  'bulletList',
  'orderedList',
  'link',
];

// Supported slash menu actions
const PAYMENT_EDITOR_SLASH_MENU_ACTIONS = [
  'heading2',
  'heading3',
  'text',
  'bulletList',
  'orderedList',
];

interface Props {
  description: string;
  onContentChange: (editor: Editor) => void;
  onUpdateTipTapEditor?: (edited: Editor | null) => void;
  className?: string;
}

export const PaymentRichTextEditor: React.FC<Props> = ({
  description,
  onContentChange,
  onUpdateTipTapEditor,
  className,
}) => {
  const tiptapEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      ClearMarksOnEnter,
      Placeholder.configure({
        emptyEditorClass:
          'before:content-[attr(data-placeholder)] before:float-left before:text-[#9B9FA3] before:h-0 before:pointer-events-none',
        placeholder: '',
      }),
      Underline,
      Link,
      ListKeymap,
      SlashCommand.configure({
        suggestion: {
          items: getSlashMenuSuggestionItems,
          render: () =>
            SlashMenuRenderer({
              Component: AssemblySlashMenu,
              ComponentProps: {
                overrideActions: PAYMENT_EDITOR_SLASH_MENU_ACTIONS,
              },
              tippyProps: {
                placement: 'bottom-start',
              },
            }),
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: clsx(
          className,
          'flex flex-col gap-1 mt-1 min-h-[64px] relative',
        ),
      },
    },
    content: description,
    onCreate: ({ editor }) => onContentChange(editor),
    onUpdate: ({ editor }) => onContentChange(editor),
  });

  useEffect(() => {
    onUpdateTipTapEditor?.(tiptapEditor);
  }, [onUpdateTipTapEditor, tiptapEditor]);

  if (!tiptapEditor) return null;

  return (
    <div
      className={clsx(
        'relative box-border border border-solid rounded [&>div>div]:!z-modal',
        {
          'border-black-heading': tiptapEditor?.isFocused,
          'border-non-hover-border': !tiptapEditor?.isFocused,
        },
      )}
    >
      <RichTextInput
        editor={tiptapEditor}
        showHighlightToolbar={false}
        supportActions={{
          toolbar: PAYMENT_EDITOR_TOOLBAR_ACTIONS,
        }}
        classes={{
          container: 'px-3.5 py-3 max-h-64 overflow-auto',
          toolbar: 'rounded-t',
        }}
      />
    </div>
  );
};
